'use client';
import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';

import Link from '@eliadmin/core/src/components/link';

import type { FetchProductQuery } from '@eliadmin/core/src/api/products/product-api';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { Heading } from './heading';
import { fetchSegmentFirstProducts } from '../homepage/fetch-segment-first-products';

export const ProductsSwiper = ({
  title,
  products,
  id,
  hideButton = false,
  href,
  locale,
  query,
  segmentSlug,
}: {
  title: string;
  products: ProductDisplayData[];
  id?: string;
  hideButton?: boolean;
  href?: string;
  locale?: string;
  query?: FetchProductQuery;
  segmentSlug?: string;
}) => {
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const personalizedProductsQuery = useQuery<ProductDisplayData[]>({
    queryKey: [
      CacheKey.PRODUCT_SEGMENT_PRODUCTS,
      'homepage-segment-first',
      segmentSlug,
      locale,
      query,
      me?.customer.id ?? 'guest',
    ],
    queryFn: () =>
      fetchSegmentFirstProducts({
        locale: locale!,
        segmentSlug: segmentSlug!,
        query: query!,
        authToken,
      }),
    enabled: isInitialized && !!authToken && !!locale && !!query && !!segmentSlug,
    placeholderData: products,
    staleTime: 0,
  });
  const visibleProducts = personalizedProductsQuery.data ?? products;

  return (
    <div
      id={id ?? undefined}
      className={cn(
        'custom-container',
        'flex flex-col items-center justify-center gap-8 sm:gap-12',
      )}
    >
      <Heading title={title} />
      <Carousel
        className={{
          container: 'w-full gap-4',
          wrapper: 'w-full',
          main: 'w-full',
          slide: 'w-[296px]',
        }}
        slides={visibleProducts.map(product => (
          <ProductCard product={product} imageSizes="270px" key={product.slug} />
        ))}
      />
      {!hideButton && href && (
        <Button asChild variant={'dark'} className="w-full sm:w-fit">
          <Link prefetch={false} href={href}>
            Zobraziť všetko
          </Link>
        </Button>
      )}
    </div>
  );
};
